import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { DashboardToolbarFab, DashboardToolbarItem } from './dashboard-toolbar.types';

@Component({
  selector: 'app-dashboard-toolbar',
  templateUrl: './dashboard-toolbar.component.html',
  styleUrls: ['./dashboard-toolbar.component.scss'],
  standalone: false
})
export class DashboardToolbarComponent implements OnInit, OnDestroy {
  /** Navigation items rendered as tabs. The consumer owns the config. */
  @Input() items: DashboardToolbarItem[] = [];
  /** Optional central FAB. When null the toolbar renders a flat row of items. */
  @Input() fab: DashboardToolbarFab | null = null;
  /** Emitted when the FAB is tapped while its destination is already active. */
  @Output() fabClick = new EventEmitter<void>();

  public activeId: string | null = null;
  public isFabActive = false;
  public fabOpen = false;

  private sub?: Subscription;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.computeActive(this.router.url);

    this.sub = this.router.events
      .pipe(filter((e: RouterEvent) => e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e: RouterEvent) => {
        if (e instanceof NavigationStart && this.fab?.activeUrl) {
          const goingToActive = e.url.includes(this.fab.activeUrl);
          const leavingActive = this.isFabActive && !goingToActive;

          if (goingToActive) this.fabOpen = true;
          if (leavingActive) this.fabOpen = false;
        }

        if (e instanceof NavigationEnd) {
          this.computeActive(e.urlAfterRedirects);
          this.fabOpen = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Left half of the items when a FAB splits the toolbar; all items otherwise. */
  get leftItems(): DashboardToolbarItem[] {
    if (!this.fab) return this.items;
    return this.items.slice(0, Math.ceil(this.items.length / 2));
  }

  /** Right half of the items, only relevant when a FAB is present. */
  get rightItems(): DashboardToolbarItem[] {
    if (!this.fab) return [];
    return this.items.slice(Math.ceil(this.items.length / 2));
  }

  async onItemClick(item: DashboardToolbarItem): Promise<void> {
    this.fabOpen = false;
    // Mark active only after a successful navigation; computeActive() on
    // NavigationEnd remains the source of truth and reconciles on route changes.
    const navigated = await this.router.navigateByUrl(item.url);
    if (navigated) {
      this.activeId = item.id;
    }
  }

  onFabClick(): void {
    if (!this.fab) return;

    if (!this.fab.activeUrl) {
      this.fabClick.emit();
      return;
    }

    if (this.isFabActive) {
      this.fabClick.emit();
    } else {
      this.router.navigateByUrl(this.fab.activeUrl);
    }
  }

  private computeActive(url: string): void {
    this.isFabActive = !!this.fab?.activeUrl && url.includes(this.fab.activeUrl);

    if (this.isFabActive) {
      this.activeId = null;
      return;
    }

    const match = this.items
      .filter((item) => url.includes(item.url))
      .sort((a, b) => b.url.length - a.url.length)[0];

    this.activeId = match ? match.id : null;
  }
}
